import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import AppShell from "@/components/AppShell/AppShell"
import Button from "@/components/Button/Button"
import DataTable from "@/components/DataTable/DataTable"
import type { Column } from "@/components/DataTable/DataTable"
import Pill from "@/components/Pill/Pill"
import SegmentedControl from "@/components/SegmentedControl/SegmentedControl"
import StatusDot from "@/components/StatusDot/StatusDot"
import StatusScreen from "@/components/StatusScreen/StatusScreen"
import Spinner from "@/components/Spinner/Spinner"
import {
  ARCHIVE_CUSTOMERS,
  ARCHIVE_EMPTY,
  ARCHIVE_RESTORE,
  ARCHIVE_SESSIONS,
  ARCHIVE_SHELF_BLURB,
  ARCHIVE_SHELF_TITLE,
  ARCHIVE_TABS,
  ARCHIVE_TITLE,
  ARCHIVE_WITH_CUSTOMER,
  TABLE_REMOVED,
  archivedWithCustomer,
  removedByLine,
} from "@/content/archive"
import { UNTITLED_SESSION } from "@/content/customers"
import { TABLE_CUSTOMER, TABLE_SESSION, TABLE_STATUS } from "@/content/nav"
import { useExpiryTick } from "@/hooks/useExpiryTick"
import { invalidateFacilitatorIndex } from "@/hooks/useFacilitatorIndex"
import type { ArchivedSession, FacilitatorIndex } from "@/hooks/useFacilitatorIndex"
import { useFirestoreWrite } from "@/hooks/useFirestoreWrite"
import { restoreCustomer } from "@/services/customers"
import { getFacilitators } from "@/services/facilitators"
import { restoreSession } from "@/services/sessions"
import { customerDetailPath, reportPath, sessionPath } from "@/utils/routePaths"
import { shortDate } from "@/utils/relativeTime"
import { sessionState } from "@/utils/sessionLifecycle"
import { REMOVAL_KINDS } from "@/types"
import type { Customer, Facilitator, RemovalKind } from "@/types"
import styles from "@/styles/pages/IndexPage.module.css"

/**
 * Everything taken out of the working lists, and the way back.
 *
 * Two shelves, not one. Archive and Delete are the same soft write underneath —
 * see `services/customers.removeCustomer` — but they are different DECISIONS,
 * and a single list would make "we stopped working with them in March" and "this
 * was a typo" look like the same fact. The segmented control is the distinction,
 * and it is the only place it is visible.
 *
 * It costs no read. `useFacilitatorIndex` already holds both lists for the nav,
 * and it is what splits the working rows from these — so the Archive is a second
 * view of a query that has already run rather than a fifth destination with its
 * own scan. That is the bar a new nav destination has to clear here.
 */
export default function ArchivePage() {
  return <AppShell>{(index) => <Archive index={index} />}</AppShell>
}

function Archive({ index }: { index: FacilitatorIndex }) {
  const navigate = useNavigate()
  const [shelf, setShelf] = useState<RemovalKind>("archived")
  const { write, isPending } = useFirestoreWrite()
  const { archivedCustomers, archivedSessions, error, loading, reload } = index
  const removers = useRemovers(archivedCustomers, archivedSessions)
  /* Only the inherited rows have a deadline that can still pass. A row removed
     on its own reads `complete` or `removed` whatever the clock says, so giving
     it a timeout would schedule a render that changes nothing. */
  const now = useExpiryTick(
    (archivedSessions ?? []).map((row) => (row.inherited ? row.session.expiresAt : null))
  )

  if (error !== null && archivedCustomers === null) {
    return (
      <StatusScreen nested eyebrow={ARCHIVE_TITLE} title="The archive did not load." raw={error}>
        <Button loading={loading} onClick={reload}>
          Try again
        </Button>
      </StatusScreen>
    )
  }

  if (archivedCustomers === null || archivedSessions === null) {
    return <StatusScreen nested eyebrow={ARCHIVE_TITLE} title="Loading the archive." waiting />
  }

  const customers = archivedCustomers.filter((customer) => customer.removedAs === shelf)
  const sessions = archivedSessions.filter((row) => row.state === shelf)

  const restore = (run: () => Promise<void>, key: string) =>
    write(async () => {
      await run()
      invalidateFacilitatorIndex()
      index.reload()
    }, key)

  const customerColumns: readonly Column<Customer>[] = [
    {
      key: "customer",
      header: TABLE_CUSTOMER,
      width: "34%",
      render: (customer) => (
        <span className={styles.strong}>{customer.name || "Untitled customer"}</span>
      ),
    },
    {
      key: "sector",
      header: "Sector",
      width: "24%",
      render: (customer) => customer.sector || "—",
    },
    {
      key: "removed",
      header: TABLE_REMOVED,
      width: "18%",
      render: (customer) => (
        <>
          {shortDate(customer.removedAt)}
          <RemovedBy uid={customer.removedBy} removers={removers} />
        </>
      ),
    },
    {
      key: "action",
      header: "",
      align: "right",
      render: (customer) => (
        <span className={styles.rowActions}>
          {/* Archived only, like Restore beside it. An archived customer's page
              is where you go to look them over and put them back; a deleted one
              has neither, so the link would lead to a page offering nothing. */}
          {shelf === "archived" ? (
            <button
              type="button"
              className={styles.link}
              onClick={() => navigate(customerDetailPath(customer.id))}
            >
              Open
            </button>
          ) : null}
          {/* Only off the Archived shelf. Delete is the one act the app does not
              undo, which the shelf's own blurb says out loud — so the missing
              button reads as an answer rather than a gap. */}
          {shelf === "archived" ? (
            <RestoreButton
              busy={isPending(`customer:${customer.id}`)}
              onClick={() => restore(() => restoreCustomer(customer.id), `customer:${customer.id}`)}
            />
          ) : null}
        </span>
      ),
    },
  ]

  const sessionColumns: readonly Column<ArchivedSession>[] = [
    {
      key: "session",
      header: TABLE_SESSION,
      width: "26%",
      render: (row) => (
        <span className={styles.strong}>{row.session.name || UNTITLED_SESSION}</span>
      ),
    },
    {
      key: "customer",
      header: TABLE_CUSTOMER,
      width: "22%",
      render: (row) => row.session.customerName || "Untitled customer",
    },
    {
      key: "status",
      header: TABLE_STATUS,
      width: "14%",
      render: (row) => <StatusDot state={sessionState(row.session, now)} compact />,
    },
    {
      key: "removed",
      header: TABLE_REMOVED,
      width: "16%",
      render: (row) =>
        row.inherited ? (
          <Pill title={archivedWithCustomer(row.customer?.name ?? "")}>
            {ARCHIVE_WITH_CUSTOMER}
          </Pill>
        ) : (
          <>
            {shortDate(row.session.removedAt)}
            <RemovedBy uid={row.session.removedBy} removers={removers} />
          </>
        ),
    },
    {
      key: "action",
      header: "",
      align: "right",
      render: (row) => (
        <span className={styles.rowActions}>
          {/* A finished session's value IS its debrief, and `/reports` reads the
              job documents directly without caring that the session was removed —
              so the report stays readable off BOTH shelves. That is the whole
              reason removing keeps things instead of destroying them.
              Open does not: the session screen of a removed session only says it
              ended, and on the Deleted shelf there is no Restore on it either, so
              it is a link to a dead end. Archived keeps it — that screen is where
              a facilitator standing in an old room puts it back.
              Not on an inherited row, for the same reason it has no Restore:
              nothing was written to that session, so the screen it opens is the
              live, writable room with no sign it is filed away. Restore the
              customer instead. */}
          {sessionState(row.session, now) === "complete" ? (
            <button
              type="button"
              className={styles.link}
              onClick={() => navigate(reportPath(row.session.id))}
            >
              Report
            </button>
          ) : shelf === "archived" && !row.inherited ? (
            <button
              type="button"
              className={styles.link}
              onClick={() => navigate(sessionPath(row.session.id))}
            >
              Open
            </button>
          ) : null}
          {/* No Restore while the CUSTOMER is removed, which is wider than
              `inherited`. An inherited row has nothing to undo. A row removed on
              its own under a removed customer does — but undoing it hands the
              row straight back to its customer's removal, so it either stays put
              wearing the pill or, if the customer was deleted, jumps to the
              Deleted shelf. Restore the customer instead. */}
          {row.inherited || row.customerRemoved || shelf === "deleted" ? null : (
            <RestoreButton
              busy={isPending(`session:${row.session.id}`)}
              onClick={() =>
                restore(() => restoreSession(row.session.id), `session:${row.session.id}`)
              }
            />
          )}
        </span>
      ),
    },
  ]

  return (
    <>
      <header className={styles.head}>
        <h1 className={styles.title}>{ARCHIVE_SHELF_TITLE[shelf]}</h1>
        <p className={styles.blurb}>{ARCHIVE_SHELF_BLURB[shelf]}</p>
      </header>

      <div className={styles.controls}>
        <SegmentedControl
          options={REMOVAL_KINDS.map((kind) => ({ value: kind, label: ARCHIVE_TABS[kind] }))}
          value={shelf}
          onChange={setShelf}
          label="Archived or deleted"
          variant="loose"
          size="sm"
        />
      </div>

      {customers.length === 0 && sessions.length === 0 ? (
        <div className={styles.empty}>{ARCHIVE_EMPTY[shelf]}</div>
      ) : (
        <>
          {customers.length > 0 ? (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>{ARCHIVE_CUSTOMERS}</h2>
              <DataTable
                caption={`${ARCHIVE_TABS[shelf]} customers`}
                columns={customerColumns}
                rows={customers}
                rowKey={(customer) => customer.id}
              />
            </section>
          ) : null}

          {sessions.length > 0 ? (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>{ARCHIVE_SESSIONS}</h2>
              <DataTable
                caption={`${ARCHIVE_TABS[shelf]} sessions`}
                columns={sessionColumns}
                rows={sessions}
                rowKey={(row) => row.session.id}
              />
            </section>
          ) : null}
        </>
      )}
    </>
  )
}

function RestoreButton({ busy, onClick }: { busy: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className={styles.link}
      disabled={busy}
      aria-busy={busy || undefined}
      onClick={onClick}
    >
      {busy ? <Spinner size="sm" /> : null}
      {ARCHIVE_RESTORE}
    </button>
  )
}

function RemovedBy({ uid, removers }: { uid: string | null; removers: Map<string, Facilitator> }) {
  const who = uid === null ? undefined : removers.get(uid)
  if (!who) return null
  return <span className={styles.subline}>{removedByLine(who.displayName || who.email)}</span>
}

function useRemovers(
  customers: Customer[] | null,
  sessions: ArchivedSession[] | null
): Map<string, Facilitator> {
  const [people, setPeople] = useState<Facilitator[]>([])
  const uids = [
    ...new Set(
      [
        ...(customers ?? []).map((customer) => customer.removedBy),
        ...(sessions ?? []).map((row) => (row.inherited ? null : row.session.removedBy)),
      ].filter((entry): entry is string => entry !== null)
    ),
  ].sort()
  const key = uids.join(",")

  useEffect(() => {
    if (key === "") return
    let live = true
    getFacilitators(key.split(",")).then(
      (loaded) => {
        if (live) setPeople(loaded)
      },
      () => {}
    )
    return () => {
      live = false
    }
  }, [key])

  return new Map(people.map((person) => [person.id, person]))
}
